import { useLayoutEffect } from 'react';

/**
 * UI scaling by resolution — a port of the reference's `applyUiZoom`
 * (game shell, bundle main-WMRRBJDP):
 *
 *   zoom      = max(0.65, min(1, innerWidth / 1240, innerHeight / 780))
 *   fontScale = 1 + (1 − zoom) × 0.6
 *
 * written on :root as `--ui-zoom` / `--ui-font-scale` on every resize. CSS
 * applies `zoom: var(--ui-zoom)` to the top bar, the bottom HUD and every
 * window (`.ui-scaled`, added by the window manager), and raises the font
 * size by `--ui-font-scale` so text stays readable (hud-hz.css, vite.config).
 * Large screens are never zoomed in (zoom ≤ 1).
 *
 * Phones keep zoom 1 (reference: ≤ 480 px wide or tall). Our phone layout
 * starts at 720 px wide, so that whole band keeps zoom 1 as well — the
 * reference would zoom a 481–720 px window to 0.65 on its desktop layout.
 */
export const UI_ZOOM_WIDTH = 1240;
export const UI_ZOOM_HEIGHT = 780;
export const UI_ZOOM_MIN = 0.65;
export const UI_FONT_GAIN = 0.6;
/** Our phone layout (CSS max-width: 720px) and the reference's short-screen mode. */
const PHONE_WIDTH = 720;
const PHONE_HEIGHT = 480;

export function uiZoomFor(width: number, height: number): number {
  if (width <= PHONE_WIDTH || height <= PHONE_HEIGHT) return 1;
  return Math.max(UI_ZOOM_MIN, Math.min(1, width / UI_ZOOM_WIDTH, height / UI_ZOOM_HEIGHT));
}

export function uiFontScaleFor(zoom: number): number {
  return 1 + (1 - zoom) * UI_FONT_GAIN;
}

/** Current UI zoom for the window size (the value the CSS uses while the game is shown). */
export function currentUiZoom(): number {
  if (typeof window === 'undefined') return 1;
  return uiZoomFor(window.innerWidth, window.innerHeight);
}

/**
 * Effective zoom of an element (reference `Oe(e) = rect.width / offsetWidth`):
 * getBoundingClientRect is in screen px, offset* in the element's own
 * (unzoomed) CSS px. 1 for hidden / empty elements.
 */
export function zoomOf(el: Element | null | undefined): number {
  if (!el || !(el instanceof HTMLElement) || el.offsetWidth <= 0) return 1;
  const ratio = el.getBoundingClientRect().width / el.offsetWidth;
  return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
}

export function applyUiZoom(): number {
  const zoom = currentUiZoom();
  const root = document.documentElement;
  root.style.setProperty('--ui-zoom', `${zoom}`);
  root.style.setProperty('--ui-font-scale', `${uiFontScaleFor(zoom)}`);
  return zoom;
}

/** Game screen: keep `--ui-zoom` / `--ui-font-scale` in step with the window size. */
export function useUiZoom(): void {
  useLayoutEffect(() => {
    applyUiZoom();
    window.addEventListener('resize', applyUiZoom);
    return () => {
      window.removeEventListener('resize', applyUiZoom);
      document.documentElement.style.removeProperty('--ui-zoom');
      document.documentElement.style.removeProperty('--ui-font-scale');
    };
  }, []);
}
